"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.MachineIcon = exports.MachineToolIcon = exports.GenericIcon = exports.EquatorXIcon = exports.EquatorIcon = exports.CMMIcon = exports.AM500UltraIcon = exports.AM500MIcon = exports.AM500FlexIcon = exports.AM400Icon = exports.AM250Icon = void 0;
/*
 * © 2024 Renishaw plc. All rights reserved.
 * This source file is the confidential property and copyright of Renishaw plc
 * Reproduction or transmission in whole or in part, in any form or
 * by any means, electronic, mechanical or otherwise, is prohibited
 * without the prior written consent of the copyright owner.
 */
const react_1 = __importDefault(require("react"));
const machine_icon_am250_1 = __importDefault(require("./svg/machine-icon-am250"));
const machine_icon_am400_1 = __importDefault(require("./svg/machine-icon-am400"));
const machine_icon_cmm_1 = __importDefault(require("./svg/machine-icon-cmm"));
const machine_icon_equator_1 = __importDefault(require("./svg/machine-icon-equator"));
const machine_icon_equatorX_1 = __importDefault(require("./svg/machine-icon-equatorX"));
const machine_icon_generic_1 = __importDefault(require("./svg/machine-icon-generic"));
const machine_icon_machinetool_1 = __importDefault(require("./svg/machine-icon-machinetool"));
const machine_icon_renam500_flex_1 = __importDefault(require("./svg/machine-icon-renam500-flex"));
const machine_icon_renam500_ultra_1 = __importDefault(require("./svg/machine-icon-renam500-ultra"));
const machine_icon_renam500m_1 = __importDefault(require("./svg/machine-icon-renam500m"));
const narwhal_1 = require("@centralwebteam/narwhal");
/** This component is intended to reduce the DOM burden of inserting large SVG files many times over,
 * either embedded in the page or (as we were previously doing with these machine icons) base64-encoded in `img` elements.
 * It inserts each SVG only once, into a hidden element (with the ID "svgrefs").
 * Then, wherever the image is required in the page, it inserts an SVG with a `use` element linking to the hidden data.
 * @param id the in-page ID for the SVG data. Conventionally, this matches the filename of the source.
 * @param size these icons are square; the size controls both the height and width.
 * @param title alt text, which is inserted on the instance rather than the reference, so that it can vary.
 * @param svgData string containing the whole SVG. It should have an id matching `id`, and no height or width.
 * */
// prettier-ignore
const SingletonSVG = ({ id, size = 36, title, svgData, className, }) => {
    const refId = "svgrefs";
    react_1.default.useInsertionEffect(() => {
        // Add the SVG data to an invisible reference element, if it doesn't already exist
        if (!document.getElementById(id)) {
            if (!document.getElementById(refId)) {
                const newRef = document.createElement("div");
                newRef.hidden = true;
                newRef.id = refId;
                document.body.appendChild(newRef);
            }
            // Add the SVG reference element outside the main React tree so that it persists
            const ref = document.getElementById(refId);
            const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
            ref === null || ref === void 0 ? void 0 : ref.appendChild(svg);
            svg.outerHTML = svgData;
        }
    });
    // Display the SVG with a `use` element.
    return (react_1.default.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", width: size, height: size, viewBox: `0 0 ${size} ${size}`, className: className },
        title && react_1.default.createElement("title", null, title),
        react_1.default.createElement("use", { href: `#${id}` })));
};
const AM250Icon = (props) => SingletonSVG({
    ...props,
    id: "machine-icon-am250",
    svgData: machine_icon_am250_1.default,
});
exports.AM250Icon = AM250Icon;
const AM400Icon = (props) => SingletonSVG({
    ...props,
    id: "machine-icon-am400",
    svgData: machine_icon_am400_1.default,
});
exports.AM400Icon = AM400Icon;
const AM500FlexIcon = (props) => SingletonSVG({
    ...props,
    id: "machine-icon-renam500-flex",
    svgData: machine_icon_renam500_flex_1.default,
});
exports.AM500FlexIcon = AM500FlexIcon;
const AM500MIcon = (props) => SingletonSVG({
    ...props,
    id: "machine-icon-renam500m",
    svgData: machine_icon_renam500m_1.default,
});
exports.AM500MIcon = AM500MIcon;
const AM500UltraIcon = (props) => SingletonSVG({
    ...props,
    id: "machine-icon-renam500-ultra",
    svgData: machine_icon_renam500_ultra_1.default,
});
exports.AM500UltraIcon = AM500UltraIcon;
const CMMIcon = (props) => SingletonSVG({
    ...props,
    id: "machine-icon-cmm",
    svgData: machine_icon_cmm_1.default,
});
exports.CMMIcon = CMMIcon;
const EquatorIcon = (props) => SingletonSVG({
    id: "machine-icon-equator",
    ...props,
    svgData: machine_icon_equator_1.default,
});
exports.EquatorIcon = EquatorIcon;
const EquatorXIcon = (props) => SingletonSVG({
    ...props,
    id: "machine-icon-equatorX",
    svgData: machine_icon_equatorX_1.default,
});
exports.EquatorXIcon = EquatorXIcon;
const GenericIcon = (props) => SingletonSVG({
    ...props,
    id: "machine-icon-generic",
    svgData: machine_icon_generic_1.default,
});
exports.GenericIcon = GenericIcon;
const MachineToolIcon = (props) => SingletonSVG({
    ...props,
    id: "machine-icon-machinetool",
    svgData: machine_icon_machinetool_1.default,
});
exports.MachineToolIcon = MachineToolIcon;
/** Machine icon for the type and model specified */
// prettier-ignore
const MachineIcon = ({ type, model, size, title, className, }) => {
    title = title !== null && title !== void 0 ? title : model;
    switch (type) {
        case narwhal_1.MachineType.CoordinateMeasurement:
        case "CMM":
        case narwhal_1.MachineType.Equator:
            return measurementModelSwitcher(type, model)({ size, title, className });
        case narwhal_1.MachineType.MachineTool:
            return (0, exports.MachineToolIcon)({ size, title, className });
        case narwhal_1.MachineType.Additive:
            return additiveModelSwitcher(model)({ size, title, className });
        default:
            return (0, exports.GenericIcon)({ size, title, className });
    }
};
exports.MachineIcon = MachineIcon;
function additiveModelSwitcher(model) {
    const matches = /^[renam\s]*(250|400|500)[ampqds\s]*(ultra|flex)?/.exec((model === null || model === void 0 ? void 0 : model.toLowerCase()) || "");
    if (matches) {
        if (matches[2] === "ultra")
            return exports.AM500UltraIcon;
        if (matches[2] === "flex")
            return exports.AM500FlexIcon;
        if (matches[1] === "250")
            return exports.AM250Icon;
        if (matches[1] === "400")
            return exports.AM400Icon;
    }
    return exports.AM500MIcon;
}
function measurementModelSwitcher(type, model) {
    return (0, narwhal_1.isEquatorX)(model)
        ? exports.EquatorXIcon
        : (0, narwhal_1.isEquator)(type)
            ? exports.EquatorIcon
            : exports.CMMIcon;
}
//# sourceMappingURL=index.js.map